import { Map as ImmutableMap } from 'immutable';

import type { TransformedMatch } from '../matches/types';
import type { ObjectsById } from '../objects/types';
import type {
  BetsState,
  ImmutableANTLRNames,
  SSRBetsState,
  SSRStakesState,
  StakesReducerState,
  StakesState,
} from './types';

import { getStakesNamesTemplatesByLang } from 'app/configs/stakesNamesTemplates';
import type { SupportedLang } from 'app/lang';
import { antlr } from 'app/packages/lib';

import { updateField } from '../utils';
import { getFinalNames } from './getFinalNames';
import { BetEntity, StakeEntity } from './records';
import { transformBet, transformStake } from './transform';
import createANTLRNames from './utils/create-antlr-names-helper';
import { getIsANTLRErrors } from './utils/get-is-antlr-errors';

// Remove after complete transition on ANTLR ->
const ANTLR_ERRORS = getIsANTLRErrors('src/lib/betting/betting-entities/reducers/stakes/index.ts');
// Remove after complete transition on ANTLR <-

type StakesOptionalProps = Partial<{
  immutableObjects: ObjectsById;
  stakesState: StakesState;
  betsState: BetsState;
}>;

export const stakesReducer = (
  lang: SupportedLang,
  { id: matchId, stakes, removedStakes, bets, removedBets }: TransformedMatch,
  { immutableObjects, stakesState, betsState }: StakesOptionalProps,
): StakesReducerState => {
  let tempStakesState = (stakesState || ImmutableMap()).asMutable();
  let tempBetsState = (betsState || ImmutableMap()).asMutable();

  const templates = getStakesNamesTemplatesByLang(lang);
  let formatterContext: antlr.MatchNamesFormatterContext | null = null;

  // { stakeId: new Map(betKey, betId) }
  const stakeBets: { [stakeId: string]: Map<number, number> } = {};
  const stakeParams: { [stakeId: string]: Record<string, unknown> } = {};

  if (removedStakes) {
    removedStakes.forEach((stakeId) => {
      const stake = tempStakesState.get(stakeId);

      if (stake) {
        tempStakesState = tempStakesState.delete(stakeId);

        stake.bets.forEach((betId) => {
          tempBetsState = tempBetsState.delete(betId);
        });
      }
    });
  }

  if (removedBets) {
    removedBets.forEach((betId) => {
      const bet = tempBetsState.get(betId);

      if (bet && bet.key !== undefined) {
        tempBetsState = tempBetsState.delete(betId);
        const stakeBet = tempStakesState.get(bet.stakeId)?.get('bets');
        if (!stakeBet) {
          console.error('lib > betting-entities > reducers > stakes > stakesReducer: `stakeBet` not found');
          return;
        }
        tempStakesState = tempStakesState.setIn([bet.stakeId, 'bets'], stakeBet.delete(bet.key as number));
      }
    });
  }

  const langForANTLR = antlr.transformToSupportedLanguage(lang);

  // We also need to store bets that belong to a stake inside the stake
  // so it looks like this stake: { bets: { 1: {betKey: betId}, 2: {betKey: betId}} }
  if (stakes) {
    const stakesKeys = Object.keys(stakes);

    stakesKeys.forEach((stakeKey) => {
      const transformed = transformStake(stakes[stakeKey]);
      const { id: stakeId, type: stakeType, isOpen, params: transformedParams } = transformed;

      const params = transformedParams || {};
      stakeParams[stakeId] = params;

      const prevStake = tempStakesState.get(stakeId);

      if (prevStake) {
        if (isOpen !== undefined) {
          tempStakesState = updateField(tempStakesState, stakeId, 'isOpen', isOpen);
        }
        if (isOpen !== undefined) {
          tempStakesState = updateField(tempStakesState, stakeId, 'isOpen', isOpen);
        }
        if (transformed.names !== undefined) {
          tempStakesState = updateField(tempStakesState, stakeId, 'names', ImmutableMap(transformed.names));
        }
      }
      else {
        if (formatterContext === null && immutableObjects !== undefined) {
          formatterContext = new antlr.MatchNamesFormatterContext();
          formatterContext.addObjects(immutableObjects);
        }

        const template = stakeType && templates?.[stakeType] || null;
        const names = template?.names || {};

        formatterContext?.setErrorContext({ matchId, names, stakeType });

        const stakeNames = antlr
          .NamesFormatter
          .Format(
            langForANTLR,
            formatterContext,
            params,
            names,
            {
              showErrors: ANTLR_ERRORS,
            });

        const finalNames = getFinalNames(stakeNames, transformed);

        tempStakesState = tempStakesState.set(
          stakeId,
          StakeEntity({
            ...transformed,
            matchId,
            params: transformedParams ? ImmutableMap(transformedParams) : undefined,
            names: ImmutableMap(finalNames) as ImmutableANTLRNames,
          }),
        );
      }
    });
  }

  if (bets) {
    const betsKeys = Object.keys(bets);

    betsKeys.forEach((betKey) => {
      const transformed = transformBet(bets[betKey]);
      const { id: betId, type: betType, key, isOpen, price, stakeId } = transformed;

      const prevBet = tempBetsState.get(betId);

      if (prevBet) {
        if (price !== undefined) {
          tempBetsState = updateField(tempBetsState, betId, 'price', price);
        }
        if (isOpen !== undefined) {
          tempBetsState = updateField(tempBetsState, betId, 'isOpen', isOpen);
        }
        if (transformed.names !== undefined) {
          tempBetsState = updateField(tempBetsState, betId, 'names', ImmutableMap(transformed.names));
        }
      }
      else {
        const stake = (stakeId && tempStakesState.get(stakeId)) || null;
        const stakeType = stake?.type;

        // betNames = transformed.names;
        if (formatterContext === null && immutableObjects !== undefined) {
          formatterContext = new antlr.MatchNamesFormatterContext();
          formatterContext.addObjects(immutableObjects);
        }

        const params = stakeId && stakeParams[stakeId] || stake?.params || {};

        const template = stakeType && templates?.[stakeType] || null;
        const names = key && template?.bets[key] || {};

        formatterContext?.setErrorContext({ matchId, stakeType, betType, betKey: key, names });

        const betNames = antlr
          .NamesFormatter
          .Format(
            langForANTLR,
            formatterContext,
            params,
            names,
            {
              showErrors: ANTLR_ERRORS,
            });

        const finalNames = getFinalNames(betNames, transformed);

        tempBetsState = tempBetsState.set(
          betId,
          BetEntity({
            ...transformed,
            matchId,
            names: ImmutableMap(finalNames) as ImmutableANTLRNames,
          }),
        );

        if (stakeId && key) {
          const stake = stakeBets[stakeId];

          if (stake) {
            stakeBets[stakeId] = stake.set(key, betId);
          }
          else {
            stakeBets[stakeId] = new Map().set(key, betId);
          }
        }
      }
    });
  }

  const stakeBetsKeys = Object.keys(stakeBets);

  if (stakeBetsKeys.length > 0) {
    stakeBetsKeys.forEach((key) => {
      const stakeId = Number(key);

      if (tempStakesState.has(stakeId)) {
        const bets = tempStakesState.get(stakeId)?.get('bets');
        if (!bets) {
          console.error('lib > betting-entities > reducers > stakes > stakesReducer: `bets` not found');
          return;
        }
        tempStakesState = tempStakesState.setIn([stakeId, 'bets'], bets.merge(ImmutableMap(stakeBets[key])));
      }
    });
  }

  return {
    stakes: tempStakesState.asImmutable(),
    bets: tempBetsState.asImmutable(),
  };
};

export const updateStakesNames = (
  lang: SupportedLang,
  immutableObjects: ObjectsById,
  stakesState: StakesState,
  betsState: BetsState,
) => {
  stakesState = stakesState.asMutable();
  betsState = betsState.asMutable();

  createANTLRNames<StakesState, BetsState>(
    lang,
    immutableObjects,
    stakesState,
    betsState,
    (type, id, names) => {
      if (type === 'stake') {
        stakesState = updateField(stakesState, id, 'names', names);
      }
      if (type === 'bet') {
        betsState = updateField(betsState, id, 'names', names);
      }
    },
  );

  return {
    stakes: stakesState.asImmutable(),
    bets: betsState.asImmutable(),
  };
};

export const transformStakesSSRState = (
  stakesState: SSRStakesState,
  betsState: SSRBetsState,
) => {
  let tempStakesState = ImmutableMap().asMutable() as StakesState;
  let tempBetsState = ImmutableMap().asMutable() as BetsState;

  Object.values(stakesState).forEach((stake) => {
    const bets = Object.keys(stake.bets).reduce((acc, key) => {
      return acc.set(parseInt(key), stake.bets[key]);
    }, ImmutableMap<number, number>());

    tempStakesState = tempStakesState.set(
      stake.id,
      StakeEntity({
        ...stake,
        bets,
        params: stake.params ? ImmutableMap(stake.params) : undefined,
        names: stake.names
          ? ImmutableMap(stake.names) as ImmutableANTLRNames
          : undefined,
      }),
    );
  });

  Object.values(betsState).forEach((bet) => {
    tempBetsState = tempBetsState.set(
      bet.id,
      BetEntity({
        ...bet,
        names: bet.names
          ? ImmutableMap(bet.names) as ImmutableANTLRNames
          : undefined,
      }),
    );
  });

  return {
    stakes: tempStakesState.asImmutable(),
    bets: tempBetsState.asImmutable(),
  };
};
